import type { RouterOutputs } from '@klear/ui/lib/trpc'
import {
  traderFormToCreateWire,
  traderFormToUpdateWire,
  type TraderForm,
  type TraderFormDirty
} from '@klear/trader/wire.client'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'
import { ROUTE_TRADER } from '@klear/constants'
import toInteger from 'lodash/toInteger'

type TraderMutationResult = NonNullable<RouterOutputs['trader']['create']>

export function useTrader(traderId?: string) {
  const isNewTrader = traderId === 'new'
  const isValidTraderId = !!(
    traderId &&
    !isNaN(parseInt(traderId)) &&
    /^\d+$/.test(traderId) &&
    toInteger(traderId) > 0
  )
  const urlPrefixApp = `/${ROUTE_TRADER}/${traderId}`

  const {
    data: trader,
    error,
    isLoading
  } = trpc.trader.get.useQuery(
    { id: isValidTraderId ? toInteger(traderId) : -1 },
    {
      enabled: isValidTraderId
    }
  )

  const invalidateQueries = ['trader.get', 'trader.list']

  const createTraderMutation = useMutationWithToast(
    trpc.trader.create.useMutation,
    createToastConfig<TraderMutationResult>('Trader', 'create', (data) => data.name),
    {
      invalidateQueries
    }
  )

  const updateTraderMutation = useMutationWithToast(
    trpc.trader.update.useMutation,
    createToastConfig<TraderMutationResult>('Trader', 'update', (data) => data.name),
    {
      invalidateQueries
    }
  )

  const createTrader = async (form: TraderForm) => {
    return createTraderMutation.mutateAsync({ data: traderFormToCreateWire(form) })
  }

  const updateTrader = async (form: TraderForm, dirty: TraderFormDirty) => {
    if (!isValidTraderId) {
      throw new Error('Cannot update trader without a valid trader id')
    }
    return updateTraderMutation.mutateAsync({
      id: toInteger(traderId),
      data: traderFormToUpdateWire(form, dirty)
    })
  }

  return {
    trader,
    error,
    isLoading,
    urlPrefixApp,
    isNewTrader,
    createTrader,
    updateTrader
  }
}
